import type { LogsInitConfiguration, datadogLogs } from "@datadog/browser-logs";
import { APP_ENV_TO_DATADOG_ENV, type AppEnv } from "@sa-nu/shared-types";

import type { LogContext } from "./context";

declare global {
  var DD_LOGS: typeof datadogLogs | undefined;
}

type DatadogLogsProps = {
  appName: string;
  appEnv: AppEnv;
  version: string | null;
  clientToken: string | null;
  /**
   * 失敗しても Datadog に送らないリクエスト先 (計測の collector など)。SDK が送り直すたびに
   * forwardErrorsToLogs が error を積み、障害や広告ブロックでエラー監視が埋まるため。
   */
  ignoredNetworkErrorOrigins?: readonly string[];
};

function canonicalOrigin(url: string): string[] {
  try {
    return [`${new URL(url).origin}/`];
  } catch {
    return [];
  }
}

/** init の設定は JSON で埋め込むので、関数の beforeSend だけ script の文字列で足す */
function beforeSendScript(origins: readonly string[]): string {
  // SDK が実際に送る URL (host は小文字) と前方一致させるため正規化する
  const prefixes = origins.flatMap(canonicalOrigin);
  if (prefixes.length === 0) return "";
  // inline script なので `</script>` で抜けられないよう < を escape する
  const literal = JSON.stringify(prefixes).replace(/</g, "\\u003c");
  return `
  config.beforeSend = function (log) {
    var origins = ${literal};
    var url = log.origin === "network" && log.http ? log.http.url : undefined;
    return !(typeof url === "string" && origins.some(function (o) { return url.indexOf(o) === 0; }));
  };`;
}

const datadogLogsSdkUrl =
  "https://www.datadoghq-browser-agent.com/ap1/v6/datadog-logs.js";

export function newDatadogLogsScript(props: DatadogLogsProps): string | null {
  const config = newDatadogLogsInitConfig(props);
  if (config == null) return null;
  const beforeSend = beforeSendScript(props.ignoredNetworkErrorOrigins ?? []);
  // https://docs.datadoghq.com/logs/log_collection/javascript/#cdn-async
  return `
(function(h,o,u,n,d) {
  h=h[d]=h[d]||{q:[],onReady:function(c){h.q.push(c)}}
  d=o.createElement(u);d.async=1;d.src=n
  n=o.getElementsByTagName(u)[0];n.parentNode.insertBefore(d,n)
})(window,document,'script','${datadogLogsSdkUrl}','DD_LOGS')
window.DD_LOGS.onReady(function() {
  var config = ${JSON.stringify(config)};${beforeSend}
  window.DD_LOGS.init(config)
  window.DD_LOGS.setGlobalContextProperty("source", "browser");
})
`;
}

function newDatadogLogsInitConfig({
  appName,
  appEnv,
  version,
  clientToken,
}: DatadogLogsProps): LogsInitConfiguration | undefined {
  if (clientToken == null) return undefined;
  return {
    clientToken,
    site: "ap1.datadoghq.com",
    service: appName,
    // Vercel Log Drain と揃えた env 名（production / integration / preview / development）
    env: APP_ENV_TO_DATADOG_ENV[appEnv] ?? appEnv,
    version: version ?? undefined,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: "all",
    sessionSampleRate: 100,
  };
}

export function datadogBrowserLogger() {
  return window.DD_LOGS?.logger;
}

/**
 * ログの `session_id`。イベントログと本番ログを join する鍵に使う。SDK は CDN から
 * 非同期に読むので、読み込み完了までは onReady だけを持つスタブが入っていて undefined。
 */
export function getBrowserLogSessionId(): string | undefined {
  const sdk: Partial<typeof datadogLogs> | undefined = window.DD_LOGS;
  if (typeof sdk?.getInternalContext !== "function") return undefined;
  return sdk.getInternalContext()?.session_id;
}

export function setBrowserLogContext(ctx: Partial<LogContext>) {
  window.DD_LOGS?.onReady(() => {
    if (window.DD_LOGS == null) return;
    window.DD_LOGS.setGlobalContext({
      ...window.DD_LOGS.getGlobalContext(),
      ...ctx,
    });
  });
}
